import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {useNavigate ,useParams} from "react-router-dom";

import Sidebar from "../../../../components/Layout/Sidebar";
import Header from "../../../../components/Layout/Header";
import Footer from "../../../../components/Layout/Footer";
import FormSkeleton from "../../../components/skeleton/FormSkeleton";

import { Form } from "react-bootstrap";
import Swal from "sweetalert2";
import Select from "react-select";

import {
  getData,
  addData,
  updateData,
  getcountries,
  getParticipantTypeData,
  getPaymentReceiptData,
  getFreeParticipantData,
  getOnsiteParticipantData,
  getParticipantById
} from "../../../../services/event/participants/participantSetupService";

import { nanoid } from "nanoid";

import { addData as addVersion } from "../../../../services/event/presentations/presentationVersionService";

import {
  addData as addComment,
  updateData as updateComment,
  deleteData as deleteComment,
} from "../../../../services/event/presentations/presentationCommentService";

import { Section ,Field,customSelectStyles} from "../../../../components/Page/common";
import { fmtDate} from "../../../../utils/dateUtils";
import { normalizeRemarks } from "../../../../utils/helper";

/* ─── Current user from auth/localStorage ──────────────────────── */
const CURRENT_USER = {
  name: localStorage.getItem("sub_domain"),
  role: localStorage.getItem("role"),
};


const createInitialFormState = (subDomain = "", series = "") => ({
  id: "",
  payment_receipt_id: "",
  uuid: "",
  payment_type: "",
  no_of_entry: "",
  name: "",
  affiliation: "",
  country: "",
  remarks: "",
  participant_type: {},
  sub_domain: subDomain,
  series,
});


/* ══════════════════════════════════════════════════════════════════
   versionsApi — backed by presentationVersionService
   Uses the same POST-based definePost pattern as all other controllers:
     Case 2: POST { presentation_uuid, field }        → filtered list
     Case 3: POST { id: "", ...fields }               → create snapshot
══════════════════════════════════════════════════════════════════ */
const versionsApi = {
  // Fetch all versions for a presentation + field  (Case 2)
  list: async (uuid, field,speech_type='presentation') => {
    try {
      const res = await addVersion({ presentation_uuid: uuid, field,speech_type });
      return Array.isArray(res?.data) ? res.data : [];
    } catch { return []; }
  },

  // Create a new version snapshot  (Case 3)
  save: async (uuid, field, content, savedBy) => {
    try {
      const res = await addVersion({
        id:                "",
        presentation_uuid: uuid,
        field,
        content,
        saved_by:          savedBy,
        speech_type:"presentation"
      });
      return res?.data || null;
    } catch { return null; }
  },
};

/* ══════════════════════════════════════════════════════════════════
   commentsApi — backed by presentationCommentService
   Uses the same POST-based definePost pattern:
     Case 2: POST { presentation_uuid, field }        → filtered list
     Case 3: POST { id: "", ...fields }               → create comment
     PUT    { id, resolved: true }                    → resolve
     DELETE { id }                                    → delete
══════════════════════════════════════════════════════════════════ */
const commentsApi = {
  // Fetch all comments for a presentation + field  (Case 2)
  list: async (uuid, field,speech_type='presentation') => {
    try {
      const res = await addComment({ presentation_uuid: uuid, field,speech_type });
      return Array.isArray(res?.data) ? res.data : [];
    } catch { return []; }
  },

  // Create a new comment or instruction  (Case 3)
  add: async (uuid, field, comment) => {
    try {
      const res = await addComment({
        id:                "",
        presentation_uuid: uuid,
        field,
        ...comment,
        speech_type:"presentation"
      });
      return res?.data || null;
    } catch { return null; }
  },

  // Resolve a comment — PUT { id, resolved: true }
  resolve: async (id) => {
    try {
      const res = await updateComment({ id, resolved: true });
      return res?.data || null;
    } catch { return null; }
  },

  // Delete a comment — DELETE { id }
  remove: async (id) => {
    try {
      const res = await deleteComment({ id });
      return res?.data || null;
    } catch { return null; }
  },
};

/* ── Styles ─────────────────────────────────────────── */
const S = {
  page: {
    background: "#f4f6f9",
    minHeight: "100vh",
    fontFamily: "'DM Sans', sans-serif",
  },
  container: { padding: "28px 28px 0" },
  topBar: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 10,
  },
  breadcrumb: {
    fontSize: 12,
    color: "#9aa5b4",
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: "uppercase",
    marginBottom: 3,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a2332",
    margin: 0,
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 18px",
    border: "1.5px solid #e3e8ef",
    borderRadius: 8,
    background: "#fff",
    color: "#4a5568",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    transition: "all 0.15s",
  },
  /* form controls */
  label: {
    fontSize: 12.5,
    fontWeight: 700,
    color: "#4a5568",
    marginBottom: 6,
    display: "block",
    letterSpacing: 0.2,
  },
  requiredStar: { color: "#c0392b", marginLeft: 2 },
  uuidWrap: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    border: "1.5px solid #e3e8ef",
    borderRadius: 8,
    overflow: "hidden",
    background: "#f8fafb",
  },
  uuidInput: {
    flex: 1,
    border: "none",
    background: "transparent",
    padding: "8px 12px",
    fontSize: 12.5,
    fontFamily: "monospace",
    color: "#6b7a8d",
    outline: "none",
  },
  uuidLabel: {
    padding: "6px 12px",
    background: "#e8f5ee",
    color: "#1a6b3c",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    borderLeft: "1px solid #d0e4d8",
  },
  /* receipt info card */
  receiptCard: {
    background: "linear-gradient(135deg, #f5fbf7 0%, #eaf5f0 100%)",
    border: "1.5px solid #c8e6d8",
    borderRadius: 10,
    padding: "16px 18px",
    marginTop: 12,
    marginBottom: 16,
  },
  receiptHeader: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#1a6b3c",
    marginBottom: 12,
  },
  receiptGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 10,
  },
  receiptItem: {
    background: "#fff",
    border: "1px solid #d8edd6",
    borderRadius: 8,
    padding: "10px 12px",
  },
  receiptItemLabel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "#7aab8a",
    marginBottom: 3,
  },
  receiptItemValue: {
    fontSize: 13.5,
    fontWeight: 500,
    color: "#1a2332",
    wordBreak: "break-all",
  },
  /* participant types */
  typesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
  },
  typeCol: {},
  typeColHeader: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: "#9aa5b4",
    marginBottom: 10,
    paddingBottom: 6,
    borderBottom: "2px solid #f0f4f8",
  },
  typeRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "7px 0",
    borderBottom: "1px solid #f8fafb",
  },
  typeLabel: {
    fontSize: 13,
    color: "#2d3748",
    fontWeight: 500,
  },
  typeInput: {
    width: 68,
    textAlign: "center",
    border: "1.5px solid #e3e8ef",
    borderRadius: 7,
    padding: "5px 8px",
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
    color: "#1a2332",
    fontWeight: 600,
    transition: "border-color 0.15s",
  },
  /* buttons */
  btnRow: {
    display: "flex",
    gap: 10,
    paddingTop: 4,
    paddingBottom: 28,
    justifyContent: "flex-end",
  },
  cancelBtn: {
    padding: "10px 22px",
    border: "1.5px solid #e3e8ef",
    borderRadius: 8,
    background: "#fff",
    color: "#4a5568",
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    transition: "all 0.15s",
  },
  submitBtn: {
    padding: "10px 28px",
    border: "none",
    borderRadius: 8,
    background: "#1a6b3c",
    color: "#fff",
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    transition: "all 0.15s",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
};

/* ══════════════════════════════════════════════════════════════════
   RemarkFieldWithFeatures
   Plain-text input with Version History + Comments stored in DB
   (same UX pattern as RichEditorWithFeatures)
══════════════════════════════════════════════════════════════════ */
function RemarkFieldWithFeatures({
  field, label, value, onChange, error, required,
  currentUser, presentationId, placeholder,type,reloadHistory,originalValue
}) {
  const [panel, setPanel]       = useState(null); // null | 'versions' | 'comments'
  const [versions, setVersions] = useState([]);
  const [comments, setComments] = useState([]);
  const [vLoading, setVLoading] = useState(false);
  const [cLoading, setCLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [commentType, setCommentType] = useState("comment");
  const [diffIdx, setDiffIdx]   = useState(null);



  const isAdmin = ["admin", "management", "onsite"].includes(currentUser?.role);
  const isPresenter = currentUser?.role === "event";

  const loadVersions = useCallback(async () => {
    if (!presentationId || presentationId === "new") return;
    setVLoading(true);
    const data = await versionsApi.list(presentationId, field);
    setVersions(Array.isArray(data) ? data : []);
    setVLoading(false);
  }, [field, presentationId]);

  const loadComments = useCallback(async () => {
    if (!presentationId || presentationId === "new") return;
    setCLoading(true);
    const data = await commentsApi.list(presentationId, field);
    setComments(Array.isArray(data) ? data : []);
    setCLoading(false);
  }, [field, presentationId]);

  useEffect(() => {
    if (type === "edit") {
      (async () => {
        await loadVersions();
        await loadComments();
      })();
    }
  }, [type, reloadHistory, loadComments, loadVersions]);

  const openPanel = (p) => {
    if (panel === p) { setPanel(null); return; }
    setPanel(p);
    if (p === "versions") loadVersions();
    if (p === "comments") loadComments();
  };

  const restoreVersion = (v) => {
    onChange({ target: { name: field, value: v.content } });
    setPanel(null);
    Swal.fire({ icon: "info", title: "Restored", text: "Remark restored. Remember to save the form.", timer: 1800, showConfirmButton: false });
  };

  const postComment = async () => {
    if (!newComment.trim()) return;
    if (!presentationId || presentationId === "new") {
      Swal.fire({ icon: "warning", title: "Save the presentation first", timer: 2200, showConfirmButton: false });
      return;
    }
    await commentsApi.add(presentationId, field, {
      author: currentUser?.name,
      role:   currentUser?.role,
      text:   newComment.trim(),
      type:   isPresenter ? "comment" : commentType,
    });
    setNewComment("");
    await loadComments();
  };

  const resolveComment = async (id) => { await commentsApi.resolve(id); await loadComments(); };
  const deleteComment = async (id) => { await commentsApi.remove(id); await loadComments(); };

  const instructionCount = comments.filter((c) => c.type === "instruction" && !c.resolved).length;

  return (
    <div>
      {/* Label + toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <label style={S.label}>
          {label}{required && <span style={S.requiredStar}>*</span>}
          {instructionCount > 0 && (
            <span style={{ marginLeft: 8, background: "#fff3cd", color: "#856404", fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 20, border: "1px solid #ffd97d" }}>
              ⚠ {instructionCount} instruction{instructionCount > 1 ? "s" : ""}
            </span>
          )}
        </label>
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" onClick={() => openPanel("versions")}
            style={{ padding: "4px 10px", fontSize: 11.5, fontWeight: 600, border: "1.5px solid #0e1f38b9", borderRadius: 6, background: panel === "versions" ? "#f0f4f8" : "#fff", color: "#4a5568", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
            🕐 History
            {versions.length > 0 && <span style={{ background: "#e3e8ef", borderRadius: 10, padding: "0 5px", fontSize: 10 }}>{versions.length}</span>}
          </button>
        </div>
      </div>

      <Form.Control
        as="textarea"
        rows={4}
        name={field}
        value={value}
        onChange={onChange}
        isInvalid={!!error}
        placeholder={placeholder}
        style={{ ...S.controlBase, borderColor: error ? "#c0392b" : "#e3e8ef" }}
      />
        {error && <div style={{ fontSize: 12, color: "#c0392b", marginTop: 4 }}>{error}</div>}

      {/* Panel */}
      {panel && (
        <div style={{ marginTop: 10, border: "1.5px solid #e3e8ef", borderRadius: 10, background: "#fff", boxShadow: "0 4px 16px rgba(0,0,0,0.07)", overflow: "hidden" }}>

          {/* Panel header */}
          <div style={{ padding: "10px 16px", borderBottom: "1px solid #f0f4f8", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafb" }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: "#1a2332" }}>
              {panel === "versions" ? "🕐 Version History — Remarks" : "💬 Comments — Remarks"}
            </span>
            <button type="button" onClick={() => setPanel(null)}
              style={{ background: "none", border: "none", fontSize: 18, color: "#9aa5b4", cursor: "pointer", lineHeight: 1 }}>×</button>
          </div>

          {/* ── Versions panel ── */}
          {panel === "versions" && (
            <div style={{ padding: 14, maxHeight: 340, overflowY: "auto" }}>
              {vLoading ? (
                <div style={{ textAlign: "center", color: "#9aa5b4", padding: 24 }}>Loading…</div>
              ) : versions.length === 0 ? (
                <div style={{ textAlign: "center", color: "#9aa5b4", padding: 24, fontSize: 13 }}>
                  No versions saved yet. Click "💾 Save Version" above to create a snapshot.
                </div>
              ) : (
                [...versions].reverse().map((v, i) => (
                  <div key={v.id || i} style={{ borderBottom: "1px solid #f0f4f8", paddingBottom: 10, marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#1a2332" }}>
                          v{versions.length - i} — {v.saved_by || "Unknown"}
                        </div>
                        <div style={{ fontSize: 11, color: "#9aa5b4", marginTop: 1 }}>{fmtDate(v.saved_at || v.createdAt)}</div>
                        <div style={{ fontSize: 12.5, color: "#4a5568", marginTop: 5, background: "#f8fafb", borderRadius: 6, padding: "5px 10px", border: "1px solid #e3e8ef", wordBreak: "break-word" }}>
                          {v.content}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 5, flexShrink: 0 }}>
                        <button type="button" onClick={() => setDiffIdx(diffIdx === i ? null : i)}
                          style={{ fontSize: 11, padding: "3px 8px", border: "1px solid #e3e8ef", borderRadius: 5, background: "#fff", cursor: "pointer", color: "#4a5568" }}>
                          {diffIdx === i ? "Hide" : "Diff"}
                        </button>
                        {isAdmin && (
                          <button type="button" onClick={() => restoreVersion(v)}
                            style={{ fontSize: 11, padding: "3px 8px", border: "1px solid #1a6b3c", borderRadius: 5, background: "#e8f5ee", cursor: "pointer", color: "#1a6b3c", fontWeight: 600 }}>
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Diff view */}
                    {diffIdx === i && (
                      <div style={{ marginTop: 8, fontSize: 12, background: "#fffbe6", border: "1px solid #ffd97d", borderRadius: 6, padding: 8 }}>
                        <div style={{ color: "#856404", fontWeight: 700, marginBottom: 4 }}>Current vs this version:</div>
                        <div style={{ color: "#c0392b", marginBottom: 3 }}>− {originalValue || "(empty)"}</div>
                        <div style={{ color: "#1a6b3c" }}>+ {v.content}</div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── Comments panel ── */}
          {panel === "comments" && (
            <div style={{ padding: 14 }}>
              {/* Thread */}
              <div style={{ maxHeight: 260, overflowY: "auto", marginBottom: 12 }}>
                {cLoading ? (
                  <div style={{ textAlign: "center", color: "#9aa5b4", padding: 20 }}>Loading…</div>
                ) : comments.length === 0 ? (
                  <div style={{ textAlign: "center", color: "#9aa5b4", padding: 20, fontSize: 13 }}>No comments yet.</div>
                ) : (
                  comments.map((c, i) => (
                    <div key={c.id || i} style={{
                      marginBottom: 10, borderRadius: 8, padding: "8px 12px",
                      background: c.type === "instruction" ? "#fffbe6" : "#f8fafb",
                      border: `1.5px solid ${c.resolved ? "#e3e8ef" : c.type === "instruction" ? "#ffd97d" : "#e3e8ef"}`,
                      opacity: c.resolved ? 0.6 : 1,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#1a2332" }}>{c.author}</span>
                          <span style={{ fontSize: 10, color: "#9aa5b4", background: "#e3e8ef", borderRadius: 4, padding: "0 5px" }}>{c.role}</span>
                      <div style={{ fontSize: 10.5, color: "#9aa5b4", marginTop: 4 }}>{fmtDate(c.created_at || c.createdAt)}</div>

                          {c.type === "instruction" && (
                            <span style={{ fontSize: 10, background: "#ffd97d", color: "#856404", borderRadius: 4, padding: "0 5px", fontWeight: 700 }}>⚠ Instruction</span>
                          )}
                          {c.resolved && (
                            <span style={{ fontSize: 10, background: "#d4edda", color: "#155724", borderRadius: 4, padding: "0 5px" }}>✓ Resolved</span>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 4 }}>
                          {isAdmin && !c.resolved && (
                            <button type="button" onClick={() => resolveComment(c.id)}
                              style={{ fontSize: 10, padding: "2px 7px", border: "1px solid #1a6b3c", borderRadius: 4, background: "#e8f5ee", color: "#1a6b3c", cursor: "pointer" }}>
                              Resolve
                            </button>
                          )}
                          {isAdmin && (
                            <button type="button" onClick={() => deleteComment(c.id)}
                              style={{ fontSize: 10, padding: "2px 7px", border: "1px solid #e3e8ef", borderRadius: 4, background: "#fff", color: "#c0392b", cursor: "pointer" }}>
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 13, color: "#2d3748", lineHeight: 1.5 }}>{c.text}</div>
                    </div>
                  ))
                )}
              </div>

              {/* New comment composer */}
              <div style={{ borderTop: "1px solid #f0f4f8", paddingTop: 10 }}>
                {isAdmin && (
                  <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                    {["comment", "instruction"].map((t) => (
                      <button key={t} type="button" onClick={() => setCommentType(t)}
                        style={{
                          fontSize: 11, padding: "3px 10px",
                          border: `1.5px solid ${commentType === t ? "#1a6b3c" : "#e3e8ef"}`,
                          borderRadius: 5,
                          background: commentType === t ? "#e8f5ee" : "#fff",
                          color: commentType === t ? "#1a6b3c" : "#4a5568",
                          cursor: "pointer", fontWeight: commentType === t ? 700 : 500,
                        }}>
                        {t === "instruction" ? "⚠ Instruction" : "💬 Comment"}
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text" value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && postComment()}
                    placeholder={isPresenter ? "Reply to instruction…" : "Add a comment or instruction…"}
                    style={{ flex: 1, border: "1.5px solid #e3e8ef", borderRadius: 7, padding: "7px 10px", fontSize: 13, fontFamily: "'DM Sans', sans-serif", outline: "none" }}
                  />
                  <button type="button" onClick={postComment}
                    style={{ padding: "7px 14px", border: "none", borderRadius: 7, background: "#1a6b3c", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                    Post
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════ */
function ParticipantSetupForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const type = id ? "edit" : "add";
  const isEdit = !!id;
  const returnUrl = "/participantSetupList";

  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [participantTypes, setParticipantTypes] = useState([]);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [dropdownData, setDropdownData] = useState({ countries: [], paymentRecipt: [] });
  const [receiptLoading, setReceiptLoading] = useState(false);
  const sub_domain = localStorage.getItem("sub_domain") || "";
  const series = localStorage.getItem("series") || "";
  const [reloadHistory, setReloadHistory] = useState(0);
  const [form, setForm] = useState(() => createInitialFormState(sub_domain, series));
  const originalFormRef = useRef({});

  
  /* ── Fetches ── */
  const fetchData = useCallback(async () => {
    try { await getData(); } catch (e) { console.error(e); }
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [country] = await Promise.all([getcountries()]);
      setDropdownData((prev) => ({ ...prev, countries: country.data }));
    } catch (e) { console.error(e); }
  }, []);

  const fetchPaymentReceipt = useCallback(async (paymentType) => {
  const subDomain = localStorage.getItem("sub_domain");
  const series = localStorage.getItem("series");

  if (!subDomain) return;

  try {
    let paymentReciptData = [];

    switch (paymentType) {
      case "free":
        paymentReciptData = await getFreeParticipantData({
          participant_type: "free",
          sub_domain: subDomain,
          series,
        });
        break;

      case "onsite":
        paymentReciptData = await getOnsiteParticipantData({
          participant_type: "onsite",
          sub_domain: subDomain,
          series,
        });
        break;

      case "paid":
        paymentReciptData = await getPaymentReceiptData({
          sub_domain: subDomain,
          series,
          event_type: "conference",
        });
        break;

      default:
        paymentReciptData = [];
    }

    paymentReciptData.sort((a, b) =>
      (a.billing_full_name || a.participant_name || "")
        .localeCompare(
          b.billing_full_name || b.participant_name || ""
        )
    );

    setDropdownData((prev) => ({
      ...prev,
      paymentRecipt: paymentReciptData,
    }));
  } catch (error) {
    console.error(error);
  }
}, []);


useEffect(() => {
  if (!form.payment_type) return;

  const loadReceipts = async () => {
    setReceiptLoading(true);

    await fetchPaymentReceipt(form.payment_type);

    setReceiptLoading(false);
  };

  loadReceipts();
}, [form.payment_type, fetchPaymentReceipt]);

  const fetchParticipantTypes = useCallback(async () => {
    try {
      const res = await getParticipantTypeData();
      setParticipantTypes(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchData(), fetchDropdownData(), fetchParticipantTypes()]);
      setLoading(false);
    };
    init();
  }, [fetchData, fetchDropdownData,fetchParticipantTypes]);

  useEffect(() => {
    if (type === "add") setForm((p) => ({ ...p, uuid: nanoid() }));
  }, [type]);
  
  const resetForm = () => {
    const newForm = createInitialFormState(form.sub_domain, form.series);

    newForm.uuid = nanoid();

    setForm(newForm);

    originalFormRef.current = {
      remarks: "",
    };
  };

    useEffect(() => {
    if (id) {
      loadParticipant(id);
    }
  }, [id]);

  const loadParticipant = async (id) => {
    if (!id) return;
    if (isEdit && type === "edit") {
    const responseData = await getParticipantById({ id });

    setForm({
      ...responseData,
      remarks:""
    });

    originalFormRef.current = {
      remarks: responseData.remarks || "",
    };
  }
    else {
      resetForm();
    }
  };


  useEffect(() => {
    if ( dropdownData?.paymentRecipt?.length) {
      const receipt = dropdownData.paymentRecipt.find(
        (item) => item.payment_id === form.payment_receipt_id
      );
      setSelectedReceipt(receipt || null);
    }
  }, [type, form.payment_receipt_id, dropdownData]);

  /* ── Handlers ── */
  const handleChange = (e) => {
  const { name, value } = e.target;

  setForm((p) => ({
    ...p,
    [name]: value,
    ...(name === "payment_type" && {
      payment_receipt_id: "",
    }),
  }));

  if (name === "payment_type") {
    setSelectedReceipt(null);

    setDropdownData((prev) => ({
      ...prev,
      paymentRecipt: [],
    }));
  }

  setErrors((p) => ({
    ...p,
    [name]: "",
  }));
};


  const handleNumberChange = (uuid, category_code, value) => {
    let val = parseInt(value);
    if (val < 1) val = 1;
    if (val > 3) val = 3;
    setForm((p) => ({
      ...p,
      participant_type: {
        ...p.participant_type,
        [uuid]: { ...p.participant_type?.[uuid], [category_code]: val },
      },
    }));
  };

  const validateForm = () => {
    const e = {};
    if (!form.name) e.name = "Name is required";
    if (!form.affiliation) e.affiliation = "Affiliation is required";
    if (!form.country) e.country = "Country is required";
    if (!form.uuid) e.uuid = "UUID is required";
    if (!form.payment_type) e.payment_type = "Payment Type is required";
    if (!form.no_of_entry) e.no_of_entry = "No of Entry is required";
    if (!form.payment_receipt_id) e.payment_receipt_id = "Payment Receipt is required";
    if (type === "edit") {
      if (!form.remarks)
        e.remarks = "Remarks is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
     try {
        const isEdit = !!form.id;
        const response = isEdit
             ? await updateData(form)
             : await addData(form);
             
        if (response.status === 200 || response.status === 201) {
              const presentationId = form.uuid;
        
              if (presentationId && presentationId !== "new") {
        
              const currentRemarks = normalizeRemarks(form.remarks);

              const participantRemarksChanged =
                currentRemarks.trim() !== "";

              if (participantRemarksChanged) {
                await versionsApi.save(
                  presentationId,
                  "remarks",
                  form.remarks,
                  CURRENT_USER.name
                );
              }

              originalFormRef.current = {
                ...originalFormRef.current,
                remarks: form.remarks,
              };
                        
              }
        
              Swal.fire({
                toast: true,
                position: "top-end",
                icon: "success",
                title: isEdit
                  ? "Participant Setup updated successfully"
                  : "Participant Setup created successfully",
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true,
              });
              setForm((p) => ({
                ...p,
                remarks:""
              }))
              
              isEdit?setReloadHistory(prev => prev + 1):navigate(returnUrl)
            }
      }
    catch(error) {
      console.error(error);
      Swal.fire({ icon: "error", title: "Error", text: "Something went wrong" });
    } finally {
      setBtnLoading(false);
    }
  };

  /* ── Derived data ── */
  const column1 = participantTypes.filter((i) => i.reference_uuid === "1");
  const column2 = participantTypes.filter((i) => i.reference_uuid === "2");
  const column3 = participantTypes.filter((i) => i.reference_uuid === "3");


  const paymentReceiptOptions = useMemo(() => {
    return dropdownData.paymentRecipt.map((item) => {
      let value = "";
      let label = "";

      switch (form.payment_type) {
        case "paid":
        case "free":
        case "onsite":
          value = item.payment_id;
          label = `${item.billing_full_name} (${item.payment_id})`;
          break;
        default:
          break;
      }

      return { value, label };
    });
  }, [dropdownData.paymentRecipt, form.payment_type]);

  const countriesOptions = useMemo(() => {
    return dropdownData?.countries?.map((item) => ({
      value: item.code,
      label: item.name,
    }));
  }, [dropdownData?.countries]);

  const colLabels = ["Group A", "Group B", "Group C"];

  const receiptFields = useMemo(() => {
    return [
      ["Payment ID", selectedReceipt?.payment_id],
      ["Full Name", selectedReceipt?.billing_full_name],
      ["Email", selectedReceipt?.billing_email],
      ["Reg Category", selectedReceipt?.reg_category],
      ["Date Of Payment", selectedReceipt?.date_of_payment],
      ["No of Ticket", selectedReceipt?.no_of_ticket],
    ].filter(([, value]) => value !== null && value !== undefined && value !== "");
  }, [selectedReceipt]);

  /* ── Render ── */
  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100" style={S.page}>
        <Header />
        {loading ? (
          <FormSkeleton />
        ) : (
          <div style={S.container}>
            {/* ── Top bar ── */}
            <div style={S.topBar}>
              <div>
                <div style={S.breadcrumb}>Conference Program &rsaquo; Participant Setup</div>
                <h2 style={S.pageTitle}>
                  {type === "add" ? "Add Participant" : "Edit Participant"}
                </h2>
              </div>
              <button
                style={S.backBtn}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#1a6b3c";
                  e.currentTarget.style.color = "#1a6b3c";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e3e8ef";
                  e.currentTarget.style.color = "#4a5568";
                }}
                onClick={() => navigate(returnUrl)}
              >
                ← Back to List
              </button>
            </div>

            {/* ── Section 1: Personal Info ── */}
            <Section icon="👤" title="Personal Information">
              <div className="row">
                <Field label="Full Name" required error={errors.name}>
                  <Form.Control
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    isInvalid={!!errors.name}
                    style={{ fontSize: 13.5, borderRadius: 8, borderColor: errors.name ? "#c0392b" : "#e3e8ef" }}
                    placeholder="Enter Full Name"
                  />
                </Field>

                <Field label="Affiliation / Institution" required error={errors.affiliation}>
                  <Form.Control
                    type="text"
                    name="affiliation"
                    value={form.affiliation}
                    onChange={handleChange}
                    isInvalid={!!errors.affiliation}
                    style={{ fontSize: 13.5, borderRadius: 8, borderColor: errors.affiliation ? "#c0392b" : "#e3e8ef" }}
                    placeholder="University / Hospital"
                  />
                </Field>

                {/* ✅ menuPortalTarget + menuPosition fix for Country select */}
                <Field label="Country" required error={errors.country}>
                  <Select
                    name="country"
                    value={countriesOptions?.find((o) => o.value === form.country?.trim())}
                    placeholder="Select a country"
                    onChange={(opt) =>
                      handleChange({ target: { name: "country", value: opt?.value || "" } })
                    }
                    options={countriesOptions}
                    styles={customSelectStyles(!!errors.country)}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </Field>

                {/* UUID */}
                <Field label="UUID" required error={errors.uuid} col="col-md-4">
                  <div style={S.uuidWrap}>
                    <input
                      style={S.uuidInput}
                      value={form.uuid}
                      readOnly
                    />
                    <span style={S.uuidLabel}>AUTO</span>
                  </div>
                </Field>

                <Field label="RegistrationType" required error={errors.payment_type} col="col-md-4">
                  <Form.Select
                    name="payment_type"
                    value={form.payment_type}
                    onChange={handleChange}
                    isInvalid={!!errors.payment_type}
                    style={{ fontSize: 13.5, borderRadius: 8, borderColor: errors.payment_type ? "#c0392b" : "#e3e8ef" }}
                  >
                    <option value="">Select</option>
                   {[
                  { value: "free", label: "Free Registration" },
                  { value: "paid", label: "Paid Registration" },
                  { value: "onsite", label: "Onsite Registration" },
                ].map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                  </Form.Select>
                </Field>

                <Field label="No of Entry" required error={errors.no_of_entry} col="col-md-4">
                  <Form.Select
                    name="no_of_entry"
                    value={form.no_of_entry}
                    onChange={handleChange}
                    isInvalid={!!errors.no_of_entry}
                    style={{ fontSize: 13.5, borderRadius: 8, borderColor: errors.no_of_entry ? "#c0392b" : "#e3e8ef" }}
                  >
                    <option value="">Select</option>
                    {[1, 2].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </Form.Select>
                </Field>
              </div>
            </Section>

            {/* ── Section 2: Participant Types ── */}
            <Section icon="🏷️" title="Participant Types">
              <div style={S.typesGrid}>
                {[column1, column2, column3].map((col, ci) => (
                  <div key={ci} style={S.typeCol}>
                    <div style={S.typeColHeader}>{colLabels[ci]}</div>
                    {col.length === 0 ? (
                      <div style={{ fontSize: 12.5, color: "#c8d0db", fontStyle: "italic", paddingTop: 8 }}>
                        No types in this group
                      </div>
                    ) : (
                      col.map((item) => (
                        <div key={item.id} style={S.typeRow}>
                          <label style={S.typeLabel}>{item.category_name}</label>
                          <input
                            type="number"
                            min={1}
                            max={3}
                            style={S.typeInput}
                            value={
                              form?.participant_type[item.reference_uuid]?.[item.category_code] || ""
                            }
                            onFocus={(e) => (e.target.style.borderColor = "#1a6b3c")}
                            onBlur={(e) => (e.target.style.borderColor = "#e3e8ef")}
                            onChange={(e) =>
                              handleNumberChange(item.reference_uuid, item.category_code, e.target.value)
                            }
                            placeholder="—"
                          />
                        </div>
                      ))
                    )}
                  </div>
                ))}
              </div>
            </Section>

            {/* ── Section 3: Payment ── */}
            <Section icon="💳" title={`${form.payment_type.charAt(0).toUpperCase() + form.payment_type.slice(1)} Registration Receipt`}>
              <div className="row">
                <Field
                  label="Registration Receipt"
                  required
                  error={errors.payment_receipt_id}
                  col="col-md-6"
                >
                  {receiptLoading ? (
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                      />
                      <span>Loading receipts...</span>
                    </div>
                  ) : (
                  <Select
                    name="payment_receipt_id"
                    value={paymentReceiptOptions?.find(
                      (o) => o.value === form.payment_receipt_id?.trim()
                    )}
                    placeholder="Select receipt ID"
                    onChange={(opt) => {
                    const value = opt?.value || "";

                    handleChange({
                      target: {
                        name: "payment_receipt_id",
                        value,
                      },
                    });

                    const receipt = dropdownData.paymentRecipt.find(
                      (item) =>
                        (form.payment_type === "paid"
                          ? item.payment_id
                          : item.participant_id) === value
                    );

                    setSelectedReceipt(receipt || null);
                  }}
                    options={paymentReceiptOptions}
                    styles={customSelectStyles(!!errors.payment_receipt_id)}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}
                </Field>
              </div>
              {receiptFields.length > 0 && (
              <div style={S.receiptCard}>
                <div style={S.receiptHeader}>Receipt Details</div>

                <div style={S.receiptGrid}>
                  {receiptFields.map(([label, value]) => (
                    <div key={label} style={S.receiptItem}>
                      <div style={S.receiptItemLabel}>{label}</div>
                      <div style={S.receiptItemValue}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </Section>

              {/* ── Section 4: Remarks ── */}
            <Section icon="📝" title="Additional Notes">
              <div className="row">
                <div className="col-md-12 mb-3">
                  <RemarkFieldWithFeatures
                    field="remarks"
                    label="Remarks"
                    value={form.remarks}
                    onChange={handleChange}
                    error={errors.remarks}
                    required = {type === "edit"}
                    placeholder="Enter remarks here..."
                    currentUser={CURRENT_USER}
                    presentationId={form.uuid || "new"}
                    type={type}
                    reloadHistory={reloadHistory}
                    originalValue={originalFormRef.current.remarks}
                  />
                </div>
            </div>
            </Section>

            {/* ── Submit row ── */}
            <div style={S.btnRow}>
              <button
                type="button"
                style={S.cancelBtn}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#c0392b";
                  e.currentTarget.style.color = "#c0392b";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e3e8ef";
                  e.currentTarget.style.color = "#4a5568";
                }}
                onClick={() => navigate(returnUrl)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{
                  ...S.submitBtn,
                  opacity: btnLoading ? 0.75 : 1,
                  cursor: btnLoading ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!btnLoading) {
                    e.currentTarget.style.background = "#2d8a55";
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(26,107,60,0.28)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#1a6b3c";
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
                onClick={handleSubmit}
                disabled={btnLoading}
              >
                {btnLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                    {type === "add" ? "Adding…" : "Updating…"}
                  </>
                ) : (
                  <>{type === "add" ? "✓ Add Participant" : "✓ Update Participant"}</>
                )}
              </button>
            </div>
          </div>
        )}
        <Footer />
      </div>
    </div>
  );
}

export default ParticipantSetupForm;